import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const VIDEO_URL = `${API_BASE_URL}/hls/stream.m3u8`;

// FRONTEND TEST ROI ONLY
// const TEST_ROI_POLYGON = [
//   [0.00, 0.74],   // left bottom start
//   [0.10, 0.66],
//   [0.18, 0.60],
//   [0.30, 0.58],
//   [0.45, 0.55],
//   [0.58, 0.54],
//   [0.70, 0.60],
//   [0.82, 0.68],
//   [0.92, 0.74],
//   [1.00, 0.88],   // right slope (avoid road)
//   [0.60, 0.92],   // bottom line
//   [0.35, 0.92],
//   [0.00, 0.92],
// ];
export default function SmoothLivePlayer() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const hlsRef = useRef(null);
  const [overlayData, setOverlayData] = useState(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls;

    const clearVideo = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = VIDEO_URL;
      video.play().catch(() => {});
    } else if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: false,
        backBufferLength: 10,
        maxBufferLength: 6,
        maxMaxBufferLength: 12,
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 6,
        enableWorker: true,
      });

      hlsRef.current = hls;
      hls.loadSource(VIDEO_URL);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        console.error("HLS error:", data);

        if (!hls) return;

        if (data?.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
          }
        }
      });
    } else {
      console.error("HLS is not supported in this browser.");
    }

    const handleWaiting = () => {
      if (hlsRef.current) hlsRef.current.startLoad();
      video.play().catch(() => {});
      clearVideo();
    };

    const handleStalled = () => {
      if (hlsRef.current) hlsRef.current.startLoad();
      video.play().catch(() => {});
      clearVideo();
    };

    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("stalled", handleStalled);

    return () => {
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("stalled", handleStalled);
      if (hls) hls.destroy();
    };
  }, []);

  useEffect(() => {
    let timer;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/live/status`);
        setOverlayData(data?.last_result || null);
      } catch (err) {
        console.error("Overlay status fetch failed", err);
      }
      timer = setTimeout(poll, 1500);
    };

    poll();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!video || !canvas || !wrap) return;

    const draw = () => {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const rect = video.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();

      const displayWidth = Math.round(rect.width);
      const displayHeight = Math.round(rect.height);

      canvas.width = displayWidth;
      canvas.height = displayHeight;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;
      canvas.style.left = `${Math.round(rect.left - wrapRect.left)}px`;
      canvas.style.top = `${Math.round(rect.top - wrapRect.top)}px`;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (video.readyState < 2) return;

      // // Draw test ROI polygon
      // if (TEST_ROI_POLYGON?.length >= 3) {
      //   ctx.beginPath();
      //   TEST_ROI_POLYGON.forEach(([nx, ny], index) => {
      //     const px = nx * displayWidth;
      //     const py = ny * displayHeight;
      //     if (index === 0) ctx.moveTo(px, py);
      //     else ctx.lineTo(px, py);
      //   });
      //   ctx.closePath();

      //   ctx.fillStyle = "rgba(255, 215, 0, 0.10)";
      //   ctx.strokeStyle = "rgba(255, 215, 0, 0.95)";
      //   ctx.lineWidth = 3;
      //   ctx.fill();
      //   ctx.stroke();

      //   ctx.font = "14px Arial";
      //   ctx.fillStyle = "rgba(255, 215, 0, 1)";
      //   const [lx, ly] = TEST_ROI_POLYGON[0];
      //   ctx.fillText("ROI TEST AREA", lx * displayWidth + 8, ly * displayHeight - 8);
      // }

      if (!overlayData || !overlayData.boxes?.length) return;

      const frameWidth = overlayData.frame_width || 1;
      const frameHeight = overlayData.frame_height || 1;
      const scaleX = displayWidth / frameWidth;
      const scaleY = displayHeight / frameHeight;

      ctx.lineWidth = 2;
      ctx.font = "14px Arial";

      overlayData.boxes.forEach((box) => {
        const x = box.x1 * scaleX;
        const y = box.y1 * scaleY;
        const w = (box.x2 - box.x1) * scaleX;
        const h = (box.y2 - box.y1) * scaleY;
        const label = box.label || "object";
        const color = label === "person" ? "#3b82f6" : "#10b981";

        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.strokeRect(x, y, w, h);

        const textWidth = ctx.measureText(label).width + 12;
        const textHeight = 22;
        const textX = x;
        const textY = Math.max(0, y - textHeight);

        ctx.fillRect(textX, textY, textWidth, textHeight);
        ctx.fillStyle = "#ffffff";
        ctx.fillText(label, textX + 6, textY + 15);
      });
    };

    draw();

    window.addEventListener("resize", draw);
    video.addEventListener("loadedmetadata", draw);
    video.addEventListener("playing", draw);
    video.addEventListener("timeupdate", draw);
    video.addEventListener("pause", draw);

    return () => {
      window.removeEventListener("resize", draw);
      video.removeEventListener("loadedmetadata", draw);
      video.removeEventListener("playing", draw);
      video.removeEventListener("timeupdate", draw);
      video.removeEventListener("pause", draw);
    };
  }, [overlayData]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden rounded-xl border bg-black"
    >
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        controls
        className="w-full h-auto block bg-black"
      />
      <canvas
        ref={canvasRef}
        className="absolute pointer-events-none"
        style={{ zIndex: 5 }}
      />
    </div>
  );
}